import type {
  CampaignPriority,
  CampaignStatus,
  CampaignType,
  CreateCampaignInput,
  CreateVariantInput,
  CreateVariantTemplateInput,
} from '~/types/graphql';
import type { Campaign, TabCampaignStatus, TabCampaignType } from '~/modules/campaign/types';
import type { ColorBackgroundAlias } from '@shopify/polaris-tokens';
import type { IListTabProps } from '~/presentation/common';
import type { ChoiceOptionItem } from '~/presentation/ui';
import type { IndexFiltersProps } from '@shopify/polaris';

import { TargetChannel, TargetDevice, TargetVisitorType } from '~/modules/campaign/types';
import { compareValues } from '~/utils';

export const DELAY_AUTO_SAVE = 1000;

export const CAMPAIGNS_QUERY_PAGE_SIZE = 100;

export const CAMPAIGN_BACKGROUND: Record<string, ColorBackgroundAlias | string> = {
  ORIGIN: '#D5E5FF', //`var(--p-color-${CAMPAIGN_BACKGROUND.ORIGIN})`,
  VARIANT: '#FDDBE2', //`var(--p-color-${CAMPAIGN_BACKGROUND.VARIANT})`,
  SUBDUED: '#F2F2F2', //`var(--p-color-${CAMPAIGN_BACKGROUND.VARIANT})`,
};

export const CAMPAIGN_BACKGROUND_MAIN: Record<string, ColorBackgroundAlias | string> = {
  ORIGIN: '#2C7DFF',
  VARIANT: '#F34A70',
};

/**
 * @DRAFT: Inactive (yellow)
 * @PAUSED: Pause (red)
 * @RUNNING: Active (green)
 * @STOPPED: Ended (gray)
 */
export const CAMPAIGN_STATUS: Record<CampaignStatus, CampaignStatus> = {
  COMPLETED: 'COMPLETED',
  DRAFT: 'DRAFT',
  PAUSED: 'PAUSED',
  RUNNING: 'RUNNING',
  STOPPED: 'STOPPED',
};

export const CAMPAIGN_ENDED_STATUS = [CAMPAIGN_STATUS.COMPLETED, CAMPAIGN_STATUS.STOPPED];

export const CAMPAIGN_PRIORITY: Record<'CONVERSION' | 'REVENUE' | 'PROFIT', CampaignPriority> = {
  CONVERSION: 'CONVERSION',
  PROFIT: 'PROFIT',
  REVENUE: 'REVENUE',
};

export const CAMPAIGN_TYPE: Record<CampaignType, CampaignType> = {
  CONTENT: 'CONTENT',
  PRICE: 'PRICE',
  TEMPLATE: 'TEMPLATE',
  TEMPLATE_BUNDLE: 'TEMPLATE_BUNDLE',
  THEME: 'THEME',
  URL: 'URL',
};

export const DEFAULT_SETTING_TARGET = ['all'];

export const CAMPAIGN_PRESET: CreateCampaignInput = {
  name: '',
  status: CAMPAIGN_STATUS.DRAFT,
  priority: CAMPAIGN_PRIORITY.CONVERSION,
  type: CAMPAIGN_TYPE.TEMPLATE,
  storeDomain: '',
  targetCountries: DEFAULT_SETTING_TARGET,
  targetDevices: DEFAULT_SETTING_TARGET,
  targetChannels: DEFAULT_SETTING_TARGET,
  targetVisitorTypes: DEFAULT_SETTING_TARGET,
};

export const VARIANT_PRESET: CreateVariantInput = {
  templateType: '',
  isWinner: false,
  isOrigin: true,
  trafficRate: 0.5,
  templateSuffix: '',
  campaignID: '',
};

export const VARIANT_TEMPLATE_PRESET: CreateVariantTemplateInput = {
  variantID: '',
  redirectURL: '',
  themeID: '',
};

export const CAMPAIGN_SEARCH_PARAMS = {
  TEMPLATE_SUFFIX: 'templateSuffix',
} as const;

export const TARGET_CHANNEL: ChoiceOptionItem<TargetChannel>[] = [
  { value: TargetChannel.DIRECT, label: 'Direct' },
  { value: TargetChannel.EMAIL, label: 'Email' },
  { value: TargetChannel.REFERRAL, label: 'Referral' },
  { value: TargetChannel.ORGANIC_SOCIAL, label: 'Organic social' },
  { value: TargetChannel.ORGANIC_SEARCH, label: 'Organic search' },
  { value: TargetChannel.PAID_SOCIAL, label: 'Paid social' },
  { value: TargetChannel.PAID_SEARCH, label: 'Paid search' },
  { value: TargetChannel.SMS, label: 'SMS' },
];

export const TARGET_DEVICES: ChoiceOptionItem<TargetDevice>[] = [
  { value: TargetDevice.DESKTOP, label: 'Desktop' },
  { value: TargetDevice.TABLET, label: 'Tablet' },
  { value: TargetDevice.MOBILE, label: 'Mobile' },
];
export const TARGET_VISITOR: ChoiceOptionItem<TargetVisitorType>[] = [
  { value: TargetVisitorType.NEW, label: 'New' },
  { value: TargetVisitorType.RETURNING, label: 'Returning' },
];
export const SETTING_GOAL: ChoiceOptionItem<CampaignPriority>[] = [
  {
    value: CAMPAIGN_PRIORITY.CONVERSION,
    label: 'Conversion',
    description: 'The rate at which visitors make a purchase',
  },
  {
    value: CAMPAIGN_PRIORITY.REVENUE,
    label: 'Revenue',
    description: 'The average revenue generated by each website visitor',
  },
  // {
  //   value: CAMPAIGN_PRIORITY.PROFIT,
  //   label: 'Profit',
  //   description:
  //     'Total profit per order and per visitor. Sync or upload your COGS for accurate profit analysis or select an alternative goal. ',
  //   inlineAction: {
  //     content: 'How to setup',
  //     url: 'https://help.shopify.com/manual/campaigns/profit',
  //   },
  // },
];

export const ACTIVE_TAB_TYPE_DEFAULT = CAMPAIGN_TYPE.TEMPLATE;

export const FILTER_CAMPAIGN_STATUS: ChoiceOptionItem<TabCampaignStatus>[] = [
  { label: 'Live', value: CAMPAIGN_STATUS.RUNNING },
  { label: 'Draft', value: CAMPAIGN_STATUS.DRAFT },
  { label: 'Paused', value: CAMPAIGN_STATUS.PAUSED },
  { label: 'Ended', value: CAMPAIGN_STATUS.STOPPED },
];
export const TAB_CAMPAIGN_TYPE: IListTabProps<TabCampaignType>[] = [
  { name: 'Template', key: CAMPAIGN_TYPE.TEMPLATE },
  { name: 'Multipage', key: CAMPAIGN_TYPE.TEMPLATE_BUNDLE },
  { name: 'Product price', key: CAMPAIGN_TYPE.PRICE },
  { name: 'URL', key: CAMPAIGN_TYPE.URL },
];

export const SORT_CAMPAIGN: IndexFiltersProps['sortOptions'] = [
  { label: 'Status', value: 'status asc', directionLabel: 'Ended to Live' },
  { label: 'Started', value: 'startedAt asc', directionLabel: 'Ascending' },
  { label: 'Created', value: 'createdAt asc', directionLabel: 'Ascending' },
  { label: 'Updated', value: 'updatedAt asc', directionLabel: 'Ascending' },
  { label: 'Started', value: 'startedAt desc', directionLabel: 'Descending' },
  { label: 'Status', value: 'status desc', directionLabel: 'Live to Ended' },
  { label: 'Created', value: 'createdAt desc', directionLabel: 'Descending' },
  { label: 'Updated', value: 'updatedAt desc', directionLabel: 'Descending' },
];

export const SORT_DEFAULT = ['status desc'];

export const compareCampaigns = (a: Campaign, b: Campaign): number => {
  const activeCompare = compareActiveCampaignsFirst(a, b);
  if (activeCompare !== 0) return activeCompare;

  const startTimeComparison = compareValues(a.startedAt, b.startedAt, 'desc');
  if (startTimeComparison !== 0) return startTimeComparison;

  return compareValues(a.updatedAt, b.updatedAt, 'desc');
};

export const compareActiveCampaignsFirst = (a: Campaign, b: Campaign): number => {
  if (a.status === CAMPAIGN_STATUS.RUNNING && b.status !== CAMPAIGN_STATUS.RUNNING) return -1;
  if (a.status !== CAMPAIGN_STATUS.RUNNING && b.status === CAMPAIGN_STATUS.RUNNING) return 1;
  return 0;
};
